import { useState, useEffect, memo, forwardRef } from 'react'

import { useSelector, useDispatch } from 'react-redux'

import { EMAIL_REGEX, FULL_NAME_REGEX, PHONE_REGEX, testValueWithRegex } from 'app/utils/regex'
import { fixNumbers } from 'app/utils/text'
import { sendDynamicFormMessage } from 'app/redux/messages/messages.actions'
import { triggerEvent } from 'app/utils/event'
import ThreeDotsLoader from 'app/components/ThreeDotsLoader'
import StatusCard from 'app/components/StatusCard'
import { ON_FORM_SUBMISSION } from 'app/constants/events'
import './styles.scss'

const getTexts = ({ widgetData }) => widgetData.widgetInfo.languageInfo.texts
const getChannelId = ({ widgetData }) => widgetData.widgetInfo.data?.channel?.id
const getUser = ({ messagesData }) => messagesData.user
const getDisableInput = ({ messagesData }) => messagesData.messages.disableInput
const getLanguageInfo = ({ widgetData }) => widgetData.widgetInfo.languageInfo

const DynamicChatForm = forwardRef(function DynamicChatForm(
  { message, selectedDepartment, setForcedFormComponent },
  ref,
) {
  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const [formInputs, setFormInputs] = useState([])

  // selectors
  const texts = useSelector(getTexts)
  const channelId = useSelector(getChannelId)
  const user = useSelector(getUser)
  const dispatch = useDispatch()
  const disableInput = useSelector(getDisableInput)

  const languageInfo = useSelector(getLanguageInfo)
  const formStatus = message.formStatus
  const cardStatus = formStatus === 'filled' ? 'success' : 'failed'
  const statusCardTexts = {
    success: languageInfo.texts.yourDetailsWasSaved,
    failed: languageInfo.texts.thereWasProblemWithSavingYourDetails,
  }

  const regexAndSpecialErrorsForSpecialInputs = {
    email: { regex: EMAIL_REGEX, message: texts.invalidEmail },
    name: { regex: FULL_NAME_REGEX, message: texts.invalidName },
    phone: { regex: PHONE_REGEX, message: texts.invalidPhone },
  }

  const getEmailFieldInitialValue = () => {
    // email that already submited for the user
    const prevUserEmail = user['email']
    // the raychat.ir check is for excluding emails that automatically set by raychat system
    if (prevUserEmail && !prevUserEmail.endsWith('@raychat.ir')) {
      return prevUserEmail
    }

    return ''
  }

  /**
   * This function checks for form fields initial values. first it checks for special fields (phone,name,email) then it checks for custom and arbitrary fields and if none found, empty string will be sent.
   *
   * @param {object} field - field
   * @return {string} field value
   *
   **/
  const getInitialValue = (field) => {
    if (field.type === 'email') return getEmailFieldInitialValue(field)

    if (field.type === 'phone') return user['phone'] || ''

    if (field.type === 'name')
      return user['name'] && testValueWithRegex(FULL_NAME_REGEX, user['name']) ? user['name'] : ''

    // checking custom fields value
    return user.info?.find((item) => item.name === field.label)?.value || ''
  }

  // as form fields are constant and do not change. we use index as an id for that field
  const getFormInputsBasedOnFields = (fields) =>
    fields.map((field, index) => ({
      id: index,
      type: field.type,
      placeholder: field.label,
      value: getInitialValue(field),
      defaultValue: '',
      error: '',
    }))

  const handleChangeFormInput = (id, value, inputType) => {
    const updatedFormInputs = formInputs.map((item) => {
      const formattedValue = inputType === 'phone' ? fixNumbers(value) : value

      if (item.id === id) return { ...item, value: formattedValue }

      return item
    })

    setFormInputs(updatedFormInputs)
  }

  const isInputValueValidByRegex = (value, regex) => {
    const inputRegex = new RegExp(regex)
    return inputRegex.test(value)
  }

  const getErrorMessage = (formInput) => {
    const specialError = regexAndSpecialErrorsForSpecialInputs[formInput.type]
    const formValue = formInput.value

    // trim value if field is string
    const value = typeof formValue === 'string' ? formValue.trim() : formValue

    if (!value) return texts.formIsEmpty

    if (specialError && !isInputValueValidByRegex(value, specialError.regex)) {
      return specialError.message
    }
  }

  const getFormInputsWithUpdatedErrors = () => {
    const updatedForms = formInputs.map((item) => {
      const error = getErrorMessage(item)

      return { ...item, error }
    })

    return updatedForms
  }

  /**
   * This function returns output for API callback when user submits the form
   *
   * @param {obj[]} values - form values
   *
   * @returns {{
   *  id: number,
   *  label: string,
   *  type: string,
   *  value: string
   * }}
   */
  const getValuesForApiSuccessCb = (values) =>
    values.map((item) => ({
      id: item.id,
      label: item.placeholder,
      value: item.value,
      type: item.type,
    }))

  const handleSubmitForm = () => {
    const updatedFormInputs = getFormInputsWithUpdatedErrors()
    const isFormInValid = updatedFormInputs.some((item) => !!item.error)

    setFormInputs(updatedFormInputs)

    if (isFormInValid) return

    const submitedFormCbValue = getValuesForApiSuccessCb(formInputs)

    // triggering event for external use of input values
    triggerEvent(ON_FORM_SUBMISSION, {
      values: submitedFormCbValue,
      formName: message.formName,
    })

    // if `notSendToServer` is true data won't be saved and only UI gets updated. it is used when `sendform` api is being called
    dispatch(
      sendDynamicFormMessage({
        data: {
          formInputs,
          teamId: selectedDepartment,
          formId: message.formId,
          notSendToServer: message.notSendToServer,
          channelId,
          setLoadingSubmit,
        },
      }),
    )

    setForcedFormComponent((prev) => ({ ...prev, done: true }))
  }

  const renderFormInputs = () =>
    formInputs
      .slice()
      .reverse()
      .map((item) => (
        <div className="form_input_wrapper" key={item.id}>
          <input
            className="form_input_box"
            type={item.type}
            placeholder={item.placeholder}
            value={item.value}
            onChange={(e) => handleChangeFormInput(item.id, e.target.value, item.type)}
          />
          {item.error && <span className="input_error">{item.error}</span>}
        </div>
      ))

  const renderSubmitBtnContent = () => {
    if (loadingSubmit) return <ThreeDotsLoader />
    const btnText = disableInput?.status ? texts.completeFormAndStartConversation : texts.submit

    return btnText
  }

  const renderSubmitBtn = () => {
    const showErrorMessage = formStatus === 'failed'

    return (
      <div className="form_submit_wrapper">
        {showErrorMessage && (
          <StatusCard
            cardType="failed"
            text={statusCardTexts[cardStatus]}
            teamId={message.teamId}
            createdAt={message.createdAt}
            msgType={message.type}
            withCloseBtn={false}
          />
        )}
        <button className="form_unfilled_actions_wrapper__button" onClick={handleSubmitForm}>
          {renderSubmitBtnContent()}
        </button>
      </div>
    )
  }

  useEffect(() => {
    if (message.fields && !formInputs.length) {
      setFormInputs(getFormInputsBasedOnFields(message.fields))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message])

  return (
    <div ref={ref} data-isforce={disableInput.status} className="form_main_container_wrapper">
      <div className="form_main_container">
        {formStatus === 'filled' ? (
          <StatusCard
            cardType={cardStatus}
            teamId={message.teamId}
            createdAt={message.createdAt}
            text={statusCardTexts[cardStatus]}
            msgType={message.type}
            withCloseBtn={true}
          />
        ) : (
          <div className="form_unfilled_wrapper">
            <div className="form_unfilled_wrapper__inner">
              <p className="form_unfilled_title">{message.title || ''}</p>
              <div className="form_unfilled_inputs_wrapper">{renderFormInputs()}</div>
              <div className="form_unfilled_actions_wrapper">{renderSubmitBtn()}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
})

export default memo(DynamicChatForm)
